"use client";

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useSession } from '@/context/SessionContext';
import { getUserRoles } from '@/lib/supabase/roles';
import { getUserStores } from '@/lib/supabase/stores';
import { useUserMenu } from '@/context/UserMenuContext';
import { useSettingsSidebar } from '@/context/SettingsSidebarContext';
import { useBankAccounts } from '@/context/BankAccountsContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, User, LogOut, Users, Folder, Printer, ListTodo, Brush, Settings, ShoppingBag, CreditCard, Pencil, Store } from '@/components/icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useScrollLock } from '@/hooks/useScrollLock';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CircleUserRound, Navigation, Copyright, ArrowRight } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import AvatarUploadNavbar from './AvatarUploadNavbar';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { usePullToClose } from '@/hooks/usePullToClose';
import { clearRememberSessionWindow, setRememberSessionFor30Days } from '@/lib/authPersistence';
import { updateProfileUsername } from '@/lib/supabase';
import { isLettersOnly, isReservedName, normalizeLettersOnlySlug } from '@/lib/utils';
import VerifiedBadge from './VerifiedBadge';

export const UserMenuSidebar: React.FC = () => {
  const { sidebarOpen, closeSidebar } = useUserMenu();
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = React.useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      closeSidebar();
      setIsClosing(false);
    }, 200);
  }, [closeSidebar]);

  const settingsSidebar = useSettingsSidebar();
  const bankAccounts = useBankAccounts();
  const { session, profile, loading, refreshProfile } = useSession();
  const { setTheme } = useTheme();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { headerHeight, isDesktop } = useHeaderOffset();

  const { toast } = useToast();

  // Login form states
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [otpResent, setOtpResent] = useState(false);
  const [rememberFor30Days, setRememberFor30Days] = useState(true);
  const formRef = useRef<HTMLFormElement>(null);
  const trimmedEmail = email.trim();
  const hasCompleteEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

  // Avatar upload state
  const [isAvatarUploadDialogOpen, setIsAvatarUploadDialogOpen] = useState(false);

  // Pull-to-close
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Auto submit when OTP is complete
  useEffect(() => {
    if (showOtp && otp.length === 6 && !loginLoading) {
      setTimeout(() => {
        if (formRef.current && otp.length === 6) {
          formRef.current.requestSubmit();
        }
      }, 0);
    }
  }, [otp, showOtp, loginLoading]);

  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: sidebarOpen && !isDesktop
  });

  useScrollLock(sidebarOpen || isClosing, false, scrollAreaRef);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target && !document.body.contains(target)) return;

      const isPortal = target.closest('[data-radix-portal]') || 
                      target.closest('[data-radix-popper-content-wrapper]') ||
                      target.closest('[role="dialog"]') ||
                      target.closest('[role="listbox"]') ||
                      target.closest('[role="menu"]');
                      
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(target) && !isPortal) {
        handleClose();
      }
    };

    if (sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen, handleClose]);

  // ESC key to close
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && sidebarOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (sidebarOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [sidebarOpen, handleClose]);

  const handleLogout = async () => {
    try {
      try {
        localStorage.removeItem('sb-' + (supabase.auth as any).apiKey + '-auth-token');
      } catch (e) {}
      clearRememberSessionWindow();
      
      await supabase.auth.signOut({ scope: 'global' });
      await new Promise(r => setTimeout(r, 200));
      
      setTheme('system');
      toast({ title: 'Sesión cerrada' });
      window.location.href = '/';
    } catch (e) {
      console.error('Logout error:', e);
      toast({ title: 'Error', description: 'No se pudo cerrar sesión', variant: 'destructive' });
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!showOtp) {
      setLoginLoading(true);
      try {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: { shouldCreateUser: true, data: {} }
        });
        if (error) throw error;
        setShowOtp(true);
      } catch (error: any) {
        console.error('OTP error:', error);
        let errorMessage = error?.message || 'Error del servidor. Por favor, intenta más tarde.';
        let errorTitle = 'Error al enviar código';

        if (error?.message?.includes('Email rate limit exceeded')) {
          const secondsMatch = error?.message?.match(/\d+/);
          const seconds = secondsMatch ? secondsMatch[0] : 'unos';
          errorMessage = `Por razones de seguridad, podrás solicitar otro código en ${seconds} segundos.`;
        }
        
        toast({ title: errorTitle, description: errorMessage, variant: 'destructive' });
      } finally {
        setLoginLoading(false);
      }
      return;
    }

    if (otp.length !== 6) {
      toast({
        title: 'Código incompleto',
        description: 'El código debe tener 6 dígitos.',
        variant: 'destructive',
      });
      return;
    }

    setLoginLoading(true);
    setLoginError(false);
    const { error } = await supabase.auth.verifyOtp({
      email,
      token: otp,
      type: 'email',
    });
    
    setLoginLoading(false);
    if (error) {
      setLoginError(true);
      setOtp('');
    } else {
      if (rememberFor30Days) {
        setRememberSessionFor30Days();
      } else {
        clearRememberSessionWindow();
      }
      setEmail('');
      setOtp('');
      setShowOtp(false);
      setLoginError(false);
      await refreshProfile();
      await new Promise(resolve => setTimeout(resolve, 500));
      window.dispatchEvent(new CustomEvent('cart-updated'));
      closeSidebar();
    }
  };

  // User state
  const currentAvatarUrl = profile?.avatar_url;
  const displayNameFromProfile = ((profile as any)?.display_name || profile?.full_name || '').trim();
  const capitalizeWords = (value: string) =>
    value
      .trim()
      .toLocaleLowerCase('es-AR')
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toLocaleUpperCase('es-AR') + word.slice(1))
      .join(' ');
  const parts = displayNameFromProfile.split(/\s+/).filter(Boolean);
  const firstName = capitalizeWords((parts[0] || '') as string);
  const lastName = capitalizeWords((parts.length > 1 ? parts.slice(1).join(' ') : '') as string);
  const username = profile?.username || '';
  const isVerifiedUser = profile?.verification_status === 'verified';
  
  const [editingField, setEditingField] = useState<'firstName' | 'lastName' | 'username' | null>(null);
  const [savingField, setSavingField] = useState<'firstName' | 'lastName' | 'username' | null>(null);
  const [draftFirstName, setDraftFirstName] = useState(firstName);
  const [draftLastName, setDraftLastName] = useState(lastName);
  const [draftUsername, setDraftUsername] = useState(username);
  const [isSeller, setIsSeller] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [usernameCheck, setUsernameCheck] = useState({
    canSave: false,
    color: 'text-muted-foreground',
    message: 'Usa solo letras, sin espacios ni numeros.',
    status: 'idle' as 'idle' | 'typing' | 'valid' | 'invalid',
  });

  const normalizedCurrentUsername = normalizeLettersOnlySlug(username);

  useEffect(() => {
    if (editingField !== 'username') {
      return;
    }

    setUsernameCheck({
      canSave: false,
      color: 'text-muted-foreground',
      message: 'Revisando username...',
      status: 'typing',
    });

    let cancelled = false;
    const timeout = setTimeout(async () => {
      const normalizedDraftUsername = normalizeLettersOnlySlug(draftUsername);
      if (!draftUsername.trim()) {
        setUsernameCheck({
          canSave: false,
          color: 'text-destructive',
          message: 'Solo letras (sin espacios ni numeros).',
          status: 'invalid',
        });
        return;
      }

      if (!normalizedDraftUsername || !isLettersOnly(normalizedDraftUsername)) {
        setUsernameCheck({
          canSave: false,
          color: 'text-destructive',
          message: 'Solo letras (sin espacios ni numeros).',
          status: 'invalid',
        });
        return;
      }

      if (isReservedName(normalizedDraftUsername)) {
        setUsernameCheck({
          canSave: false,
          color: 'text-destructive',
          message: 'Username reservado, elige otro.',
          status: 'invalid',
        });
        return;
      }

      if (normalizedDraftUsername === normalizedCurrentUsername) {
        setUsernameCheck({
          canSave: false,
          color: 'text-destructive',
          message: 'Sin cambios.',
          status: 'invalid',
        });
        return;
      }

      setUsernameCheck({
        canSave: false,
        color: 'text-muted-foreground',
        message: 'Verificando disponibilidad...',
        status: 'typing',
      });

      try {
        const { data: storeData, error: storeError } = await supabase
          .from('stores')
          .select('id')
          .eq('slug', normalizedDraftUsername)
          .maybeSingle();

        if (cancelled) return;

        if (storeError) {
          setUsernameCheck({
            canSave: false,
            color: 'text-destructive',
            message: 'Error comprobando disponibilidad.',
            status: 'invalid',
          });
          return;
        }

        if (storeData?.id) {
          setUsernameCheck({
            canSave: false,
            color: 'text-destructive',
            message: 'Ya en uso por una tienda.',
            status: 'invalid',
          });
          return;
        }

        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('username', normalizedDraftUsername)
          .maybeSingle();

        if (cancelled) return;

        if (profileError) {
          setUsernameCheck({
            canSave: false,
            color: 'text-destructive',
            message: 'Error comprobando disponibilidad.',
            status: 'invalid',
          });
          return;
        }

        if (profileData?.id && profileData.id !== session?.user?.id) {
          setUsernameCheck({
            canSave: false,
            color: 'text-destructive',
            message: 'Ese username ya está ocupado.',
            status: 'invalid',
          });
          return;
        }
      } catch (err) {
        setUsernameCheck({
          canSave: false,
          color: 'text-destructive',
          message: 'Error comprobando disponibilidad.',
          status: 'invalid',
        });
        return;
      }
      setUsernameCheck({
        canSave: true,
        color: 'text-emerald-600',
        message: `Disponible para guardar: @${normalizedDraftUsername}`,
        status: 'valid',
      });
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [draftUsername, editingField, normalizedCurrentUsername, session?.user?.id]);

  useEffect(() => {
    setDraftFirstName(firstName);
    setDraftLastName(lastName);
    setDraftUsername(username);
  }, [firstName, lastName, username]);

  const saveInlineField = React.useCallback(async (field: 'firstName' | 'lastName' | 'username') => {
    if (!session?.user?.id || !profile) return;
    if (savingField) return;

    setSavingField(field);
    try {
      if (field === 'firstName' || field === 'lastName') {
        const fn = capitalizeWords(draftFirstName);
        const ln = capitalizeWords(draftLastName);
        const displayName = [fn, ln].filter(Boolean).join(' ');
        const { error } = await supabase.from('profiles').update({ name: fn, surname: ln, display_name: displayName }).eq('id', session.user.id);
        if (error) throw error;
        setDraftFirstName(fn);
        setDraftLastName(ln);
        await refreshProfile();
        setEditingField(null);
        return;
      }

      if (field === 'username') {
        if (!usernameCheck.canSave) {
          return;
        }

        const normalized = normalizeLettersOnlySlug(draftUsername);

        if (!normalized || !isLettersOnly(normalized)) {
          toast({
            title: 'Username inválido',
            description: 'Usá solo letras (sin espacios ni números).',
            variant: 'destructive',
          });
          return;
        }

        if (isReservedName(normalized)) {
          toast({
            title: 'Username no disponible',
            description: 'Ese nombre está reservado, elegí otro.',
            variant: 'destructive',
          });
          return;
        }

        await updateProfileUsername(session.user.id, normalized);
        await refreshProfile();
        setDraftUsername(normalized);
        setEditingField(null);
      }
    } catch (error: any) {
      console.error('❌ Error saving field:', error);
      const isDuplicate = error?.code === '23505';
      toast({
        title: 'No se pudo guardar',
        description: isDuplicate
          ? 'Ese username ya existe, probá otro.'
          : 'Intentá nuevamente en unos segundos.',
        variant: 'destructive',
      });
    } finally {
      setSavingField(null);
    }
  }, [session?.user?.id, profile, savingField, draftFirstName, draftLastName, draftUsername, refreshProfile, toast, capitalizeWords, usernameCheck.canSave]);

  const cancelInlineField = (field: 'firstName' | 'lastName' | 'username') => {
    if (field === 'firstName') setDraftFirstName(firstName);
    if (field === 'lastName') setDraftLastName(lastName);
    if (field === 'username') setDraftUsername(username);
    setEditingField(null);
  };

  useEffect(() => {
    async function checkRoles() {
      if (session?.user?.id) {
        const roles = await getUserRoles(session.user.id);
        setIsAdmin(roles.includes('admin'));
        setIsSeller(roles.includes('admin') || roles.includes('partner'));
      } else {
        setIsAdmin(false);
        setIsSeller(false);
      }
    }
    checkRoles();
  }, [session?.user?.id]);

  if (loading || (session && !profile)) {
    return null;
  }

  // Logged out view
  if (!session) {
    return (
      <>
        {(sidebarOpen || isClosing) && (
          <div
            className={cn(
              'fixed inset-0 bg-black/40 z-[10000] transition-opacity duration-200',
              isClosing && 'opacity-0 pointer-events-none'
            )}
            onClick={handleClose}
          />
        )}

        <div
          ref={sidebarRef}
          className={cn(
            'fixed right-0 w-full lg:w-[400px] bg-background z-[10001] flex flex-col',
            'transition-transform duration-200',
            sidebarOpen && !isClosing ? 'translate-x-0' : 'translate-x-full',
            !sidebarOpen && 'invisible'
          )}
          style={{
            top: '0',
            height: '100dvh',
            pointerEvents: (sidebarOpen && !isClosing) ? 'auto' : 'none'
          }}
        >
          <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />

          <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5" />
              Cuenta
            </h2>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleClose(); }}
                    className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </TooltipTrigger>
                <TooltipContent variant="sidebar">
                  <p>Cerrar</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <ScrollArea ref={scrollAreaRef} className="flex-1">
            <div className="p-4 space-y-6">
              {/* Brand block */}
              <BrandBlock />

              {/* Welcome copy */}
              <div className="space-y-1">
                <h3 className="text-xl font-bold text-foreground">Bienvenido a Minibid</h3>
                <p className="text-sm text-muted-foreground">Ingresá tu email y te enviaremos un código de 6 dígitos para acceder. Si es tu primera vez, tu cuenta gratuita se creará automáticamente. Es rápido y seguro.</p>
              </div>

              {/* Login Form */}
              <div>
              <form ref={formRef} onSubmit={handleLoginSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={showOtp || loginLoading}
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="flex items-center gap-2 rounded-md p-1 -ml-1 hover:bg-muted/40 transition-colors">
                  <Checkbox
                    id="remember-30-days"
                    checked={rememberFor30Days}
                    onCheckedChange={(checked) => setRememberFor30Days(checked === true)}
                    disabled={loginLoading}
                  />
                  <Label htmlFor="remember-30-days" className="text-sm cursor-pointer">
                    Mantenerme conectado por 30 días
                  </Label>
                </div>

                {showOtp && (
                  <div className="space-y-2">
                    <Label htmlFor="otp">Código</Label>
                    <input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      autoComplete="one-time-code"
                      maxLength={6}
                      className={cn(
                        "w-full tracking-widest text-center text-2xl rounded-lg border border-input bg-background shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all",
                        loginError && "border-red-500 ring-2 ring-red-500 animate-shake"
                      )}
                      value={otp}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtp(val);
                        if (loginError) setLoginError(false);
                      }}
                      autoFocus
                      required
                    />
                    <div className="text-xs text-muted-foreground text-center">
                      Recibiste un código de 6 dígitos en <span className="font-medium">{email}</span>.
                      {otpResent && (
                        <span className="block mt-1 text-primary font-medium animate-in fade-in slide-in-from-top-1">
                          Revisa también tu carpeta de spam o correo no deseado.
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => {
                          setShowOtp(false);
                          setOtp('');
                        }}
                      >
                        Cambiar correo
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={async () => {
                          setLoginLoading(true);
                          try {
                            const { error } = await supabase.auth.signInWithOtp({
                              email,
                              options: {
                                shouldCreateUser: true,
                                data: {}
                              }
                            });
                            if (error) throw error;
                            setOtpResent(true);
                            setOtp('');
                          } catch (error: any) {
                            const rawMsg: string = error?.message || '';
                            const friendlyMsg = rawMsg.toLowerCase().includes('security purposes') || rawMsg.toLowerCase().includes('only request this after')
                              ? `Por seguridad, solo podés pedirlo dentro de ${rawMsg.match(/\d+/)?.[0] ?? 'unos'} segundos.`
                              : rawMsg || 'Intenta más tarde';
                            toast({
                              title: 'Error al reenviar código',
                              description: friendlyMsg,
                              variant: 'destructive',
                            });
                          } finally {
                            setLoginLoading(false);
                          }
                        }}
                        disabled={loginLoading}
                      >
                        Reenviar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Mostrar el botón solo si NO está en modo OTP */}
                {!showOtp && (
                  <>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loginLoading}
                    >
                      {loginLoading ? 'Enviando...' : (
                        <span className="inline-flex items-center justify-center gap-1.5 leading-none">
                          Enviar código
                          <ArrowRight className="h-4 w-4 shrink-0 animate-[bounce-x_2s_ease-in-out_infinite]" />
                        </span>
                      )}
                    </Button>

                    {hasCompleteEmail && (
                      <div className="flex justify-center pt-2">
                        <button
                          type="button"
                          disabled={loginLoading}
                          onClick={() => {
                            if (!hasCompleteEmail) {
                              toast({
                                title: 'Ingresá un email válido',
                                description: 'Necesitamos tu correo completo para validar el código.',
                                variant: 'destructive',
                              });
                              return;
                            }

                            setOtp('');
                            setLoginError(false);
                            setOtpResent(false);
                            setShowOtp(true);
                          }}
                          className="inline-flex w-fit max-w-full items-center justify-start gap-1.5 rounded-full px-0 py-0.5 text-[11px] font-normal text-muted-foreground/70 transition-colors hover:text-muted-foreground disabled:opacity-50"
                        >
                          Ya tengo un código
                        </button>
                      </div>
                    )}
                  </>
                )}
              </form>
              </div>
            </div>
          </ScrollArea>

          {!isDesktop && <div className="h-[env(safe-area-inset-bottom,0px)] bg-background w-full shrink-0" />}
        </div>
      </>
    );
  }

  // Logged in view
  return (
    <>
      {(sidebarOpen || isClosing) && (
        <div
          className={cn(
            'fixed inset-0 bg-black/40 z-[10000] transition-opacity duration-200',
            isClosing && 'opacity-0 pointer-events-none'
          )}
          onClick={handleClose}
        />
      )}

      <div
        ref={sidebarRef}
        className={cn(
          'fixed right-0 w-full lg:w-[400px] bg-background z-[10001] flex flex-col overflow-hidden',
          'transition-transform duration-200',
          sidebarOpen && !isClosing ? 'translate-x-0' : 'translate-x-full',
          !sidebarOpen && 'invisible'
        )}
        style={{
          top: '0',
          height: '100dvh',
          pointerEvents: (sidebarOpen && !isClosing) ? 'auto' : 'none'
        }}
      >
        <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />

        <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <User className="w-5 h-5" />
            Cuenta
          </h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); handleClose(); }}
                  className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent variant="sidebar">
                <p>Cerrar</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <ScrollArea ref={scrollAreaRef} className="flex-1">
          <div className="p-4 space-y-6">
            {/* Brand block */}
            <BrandBlock />

            {/* Profile */}
            <div className="rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-4 shadow-sm">
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative shrink-0">
                  {username ? (
                    <Link
                      to={`/${username}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClose();
                      }}
                      aria-label="Ir a mi perfil"
                    >
                      <Avatar className="h-16 w-16 border-2 border-primary/30 shadow-md cursor-pointer transition-opacity hover:opacity-90">
                        <AvatarImage src={currentAvatarUrl || undefined} alt={profile?.full_name || undefined} />
                        <AvatarFallback><CircleUserRound className="h-8 w-8 text-muted-foreground" /></AvatarFallback>
                      </Avatar>
                    </Link>
                  ) : (
                    <Avatar className="h-16 w-16 border-2 border-primary/30 shadow-md">
                      <AvatarImage src={currentAvatarUrl || undefined} alt={profile?.full_name || undefined} />
                      <AvatarFallback><CircleUserRound className="h-8 w-8 text-muted-foreground" /></AvatarFallback>
                    </Avatar>
                  )}
                  <button
                    type="button"
                    aria-label="Editar foto"
                    onClick={() => setIsAvatarUploadDialogOpen(true)}
                    className="absolute -bottom-1 -left-1 h-6 w-6 rounded-full border border-primary/30 bg-background text-primary shadow-sm flex items-center justify-center transition-colors hover:bg-primary/10"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-primary/80 truncate inline-flex items-center gap-1">
                    <span className="truncate">{displayNameFromProfile || 'Cuenta Minibid'}</span>
                    <VerifiedBadge verified={isVerifiedUser} size={12} className="shrink-0" />
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{profile?.email}</p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <div className="rounded-lg border border-primary/15 bg-background/70 px-3 py-2">
                  {editingField === 'firstName' ? (
                    <div className="space-y-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Nombre</p>
                        <Input
                          value={draftFirstName}
                          onChange={(e) => setDraftFirstName(e.target.value)}
                          className="mt-1 h-8 text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void saveInlineField('firstName');
                            if (e.key === 'Escape') cancelInlineField('firstName');
                          }}
                        />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Apellido</p>
                        <Input
                          value={draftLastName}
                          onChange={(e) => setDraftLastName(e.target.value)}
                          className="mt-1 h-8 text-sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void saveInlineField('firstName');
                            if (e.key === 'Escape') cancelInlineField('firstName');
                          }}
                        />
                      </div>
                      <div className="flex items-center justify-end gap-1 pt-1">
                        <button
                          type="button"
                          onClick={() => { void saveInlineField('firstName'); }}
                          disabled={savingField === 'firstName'}
                          className="text-[11px] text-primary font-semibold hover:underline disabled:opacity-50"
                        >
                          Guardar
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelInlineField('firstName')}
                          disabled={savingField === 'firstName'}
                          className="text-[11px] text-muted-foreground font-semibold hover:underline disabled:opacity-50"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Nombre</p>
                        <p className="text-sm font-semibold text-foreground truncate">{displayNameFromProfile || 'Sin nombre'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingField('firstName');
                        }}
                        className="text-primary hover:text-primary/80"
                        aria-label="Editar nombre y apellido"
                      >
                        <Pencil className="h-3.5 w-3.5 shrink-0" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-primary/15 bg-background/70 px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Username</p>
                      {editingField === 'username' ? (
                        <div className="mt-1 space-y-1">
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-muted-foreground">@</span>
                            <Input
                              value={draftUsername}
                              onChange={(e) => setDraftUsername(e.target.value)}
                              className={cn(
                                'h-8 text-sm',
                                usernameCheck.status === 'valid' && 'border-emerald-500 focus-visible:ring-emerald-500',
                                usernameCheck.status === 'invalid' && 'border-destructive focus-visible:ring-destructive'
                              )}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && usernameCheck.canSave) void saveInlineField('username');
                                if (e.key === 'Escape') cancelInlineField('username');
                              }}
                            />
                          </div>
                          <p className={cn('text-[11px] leading-tight', usernameCheck.color)}>{usernameCheck.message}</p>
                        </div>
                      ) : (
                        <div className="inline-flex max-w-full items-center gap-1">
                          <p className="text-sm font-semibold text-foreground truncate">@{username || 'sin-username'}</p>
                          <VerifiedBadge verified={isVerifiedUser} size={14} className="shrink-0" />
                        </div>
                      )}
                    </div>
                    {editingField === 'username' ? (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => { void saveInlineField('username'); }}
                          disabled={savingField === 'username' || !usernameCheck.canSave}
                          className="text-[11px] text-primary font-semibold hover:underline disabled:opacity-50"
                        >
                          Guardar
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelInlineField('username')}
                          disabled={savingField === 'username'}
                          className="text-[11px] text-muted-foreground font-semibold hover:underline disabled:opacity-50"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingField('username')}
                        className="text-primary hover:text-primary/80"
                        aria-label="Editar username"
                      >
                        <Pencil className="h-3.5 w-3.5 shrink-0" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Tools */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-2 px-3">Herramientas</h3>
              <div className="space-y-1">
                <Link to="/transporte" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors" onClick={(e) => { e.stopPropagation(); handleClose(); }}>
                  <Navigation className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Repartir</span>
                </Link>
                {isAdmin && (
                  <Link to="/print-room" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors" onClick={(e) => { e.stopPropagation(); handleClose(); }}>
                    <Printer className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Sala de Impresión</span>
                  </Link>
                )}
                <Link
                  to="/tiendas/mis-tiendas"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors w-full text-left"
                  onClick={e => { e.stopPropagation(); handleClose(); }}
                >
                  <Store className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-600">Mis Tiendas</span>
                </Link>
                {isSeller && (
                  <Link
                    to="/tiendas/cargar"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors w-full text-left"
                    onClick={e => { e.stopPropagation(); handleClose(); }}
                  >
                    <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Cargar Productos</span>
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/admin/verifications"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-primary/10 transition-colors w-full text-left"
                    onClick={e => { e.stopPropagation(); handleClose(); }}
                  >
                    <Folder className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold text-primary">Panel de Verificaciones</span>
                  </Link>
                )}
              </div>
            </div>

            <Separator />

            {/* Community */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-2 px-3">Comunidad</h3>
              <div className="space-y-1">
                <Link to="/seguidos" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors" onClick={(e) => { e.stopPropagation(); handleClose(); }}>
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Seguidos</span>
                </Link>
                <Link to="/followers" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors" onClick={(e) => { e.stopPropagation(); handleClose(); }}>
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Seguidores</span>
                </Link>
                <Link to="/activity-log" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors" onClick={(e) => { e.stopPropagation(); handleClose(); }}>
                  <ListTodo className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Registro de Actividad</span>
                </Link>
              </div>
            </div>

            <Separator />

            {/* Settings */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-2 px-3">Ajustes</h3>
              <div className="space-y-1">
                <Link to="/configuracion" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors" onClick={(e) => { e.stopPropagation(); handleClose(); }}>
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Editar tu información</span>
                </Link>
                <button
                  onClick={() => { handleClose(); bankAccounts.open(session.user.id, 'user'); }}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors w-full text-left"
                >
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Tus cuentas bancarias</span>
                </button>
                <button
                  onClick={() => { handleClose(); settingsSidebar.open(); }}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors w-full text-left"
                >
                  <Brush className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Apariencia</span>
                </button>
              </div>
            </div>

            <Separator />

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-destructive/10 transition-colors w-full text-left text-destructive"
            >
              <LogOut className="h-4 w-4" />
              <span className="text-sm">Cerrar sesión</span>
            </button>
          </div>
        </ScrollArea>

        {!isDesktop && <div className="h-[env(safe-area-inset-bottom,0px)] bg-background w-full shrink-0" />}

        {session && (
          <AvatarUploadNavbar
            isOpen={isAvatarUploadDialogOpen}
            onClose={() => setIsAvatarUploadDialogOpen(false)}
            session={session}
            currentAvatarUrl={currentAvatarUrl}
            onAvatarUpdated={refreshProfile}
          />
        )}
      </div>
    </>
  );
};

// Bloque de marca: logo Minibid con color del tema actual
const BrandBlock: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-2 gap-0.5">
    <svg
      viewBox="0 0 1356 784"
      className="text-primary"
      fill="currentColor"
      style={{ width: '7.2rem', height: 'auto', display: 'block' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="matrix(1.324219,0,0,1.324219,0,0)">
        <path d="M711.905,324.467C723.813,470.097 601.369,572.586 480.421,534.771C472.517,532.299 472.498,532.259 472.201,531.621C471.118,529.295 474.236,531.151 492.827,517.977C498.686,513.825 508.753,522.012 541.477,520.059C651.085,513.515 735.63,378.178 664.183,238.666C609.683,132.243 445.535,41.164 293.434,140.394C211.198,194.045 158.069,312.503 212.248,422.625C253.036,505.528 359.794,558.618 459.436,508.378C532.179,471.7 588.74,376.908 535.684,265.415C533.459,260.74 535.245,259.879 541.089,250.26C544.96,243.887 547.059,239.122 549.185,242.698C577.502,290.324 597.737,375.617 550.988,451.803C479.501,568.303 315.729,588.601 219.441,476.551C118.195,358.732 164.132,155.157 340.325,90.049C470.8,41.835 623.194,106.539 686.873,234.308C688.232,237.034 694.923,250.459 701.182,269.601C708.684,292.539 711.456,320.018 711.905,324.467Z" />
        <path d="M656.549,99.073C632.368,96.779 613.584,97.236 583.541,102.713C570.68,105.058 570.336,107.639 563.259,103.946C540.306,91.968 538.45,93.716 538.449,91.495C538.448,89.747 539.359,90.477 549.411,87.221C717.355,32.82 889.718,151.253 897.233,321.515C903.729,468.689 760.186,599.748 605.413,532.691C600.166,530.418 607.459,528.771 614.287,525.116C624.365,519.72 624.015,516.406 631.662,519.007C705.12,543.997 793.165,513.947 839.643,442.594C927.267,308.076 837.178,119.458 656.549,99.073Z" />
        <path d="M373.098,341.499C373.563,379.655 380.315,423.363 418.953,468.082C429.718,480.542 450.916,496.634 459.769,501.047C464.457,503.384 456.838,505.742 450.596,508.686C436.6,515.289 436.086,512.763 435.561,512.404C308.433,425.489 324.861,214.6 483.242,118.074C488.411,114.924 495.294,110.048 498.399,110.904C509.019,113.829 525.469,119.528 525.881,120.322C527.313,123.077 510.533,125.178 474.174,152.058C448.021,171.393 429.848,191.385 409.874,220.762C375.247,271.692 373.409,331.407 373.098,341.499Z" />
        <path d="M509.08,327.442C500.22,401.85 531.898,432.286 529.193,438.393C528.334,440.335 518.023,456.235 516.489,456.454C513.64,456.86 490.387,417.018 485.919,371.458C472.665,236.303 589.649,159.533 593.559,160.219C594.171,160.326 613.253,174.907 610.607,176.6C567.069,204.469 521.306,247.799 509.08,327.442Z" />
        <path d="M697.543,122.922C714.221,121.917 726.976,121.223 733.662,124.138C761.252,136.163 780.892,152.94 783.875,155.098C784.193,155.329 793.46,162.034 786.539,159.22C780.878,156.918 780.997,156.685 780.491,156.528C777.751,155.675 737.183,140.663 687.47,148.325C644.517,154.945 643.002,164.313 637.889,159.098C623.816,144.746 622.921,145.582 622.513,144.493C621.602,142.056 629.817,139.801 630.511,139.527C661.502,127.266 674.95,125.698 697.543,122.922Z" />
        <path d="M362.481,122.916C368.701,123.58 410.309,128.022 438.362,141.775C444.025,144.552 439.012,145.869 433.862,150.879C426.234,158.298 424.067,161.615 421.461,160.597C366.189,139.026 322.3,146.689 296.578,152.788C285.594,155.393 275.069,160.995 275.513,158.503C275.619,157.908 298.404,140.667 314.667,131.805C336.581,119.863 337.562,121.97 362.481,122.916Z" />
        <path d="M515.528,197.474C528.164,211.237 530.499,212.071 526.939,216.856C517.185,229.966 516.301,234.555 513.887,231.201C488.789,196.319 452.446,177.211 452.569,175.507C452.596,175.14 468.066,159.226 471.472,160.56C474.36,161.691 487.496,172.04 488.843,173.1C502.907,184.179 508.696,190.203 515.528,197.474Z" />
        <path d="M558.46,474.541C585.698,501.811 596.981,500.817 592.343,503.305C591.454,503.782 571.852,512.12 568.514,511.445C564.932,510.72 534.892,483.619 535.233,480.472C535.31,479.767 547.449,464.479 547.484,464.469C549.495,463.85 549.137,465.418 558.46,474.541Z" />
      </g>
    </svg>
    <div className="flex items-center gap-1 text-muted-foreground" style={{ fontSize: '0.65rem' }}>
      <Copyright className="w-2.5 h-2.5" />
      <span>2026 Minibid.org. Hecho por Rodrigo Márquez.</span>
    </div>
  </div>
);
